import React, { useEffect, useState } from 'react';

// Review list (order flow step 6): everything the order flow could not finish on its own, each with a plain reason
// and what to do; plus the health summary (today's arrivals, the Zoho to-do list, last nightly check).
const STATE_TABS = [['open', 'Open'], ['resolved', 'Resolved'], ['dismissed', 'Dismissed'], ['all', 'All']];
const KIND_LABEL = {
  missing_order: 'Not in Order Desk', no_so: 'No Zoho SO', so_missing_in_zoho: 'SO missing in Zoho',
  duplicate_link: 'Duplicate', invoice_failed: 'Invoice failed', zoho_task_review: 'Zoho change',
};
const when = (t) => (t ? new Date(t).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '');

export default function FlowReview({ api, toast, onOpenOrder, canAct = true }) {
  const [state, setState] = useState('open');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);
  const [kind, setKind] = useState('all');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let alive = true;
    setErr(null);
    api(`/api/v1/order-desk/review?state=${state}`).then((r) => alive && setData(r)).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, state, reload]);
  const move = async (key, next) => {
    setBusy(key);
    try { await api(`/api/v1/order-desk/review/${encodeURIComponent(key)}`, 'POST', { state: next, note: notes[key] || '' }); toast?.(next === 'open' ? 'Reopened' : `Marked ${next}`); setReload((x) => x + 1); } catch (e) { toast?.(e.message, true); } finally { setBusy(null); }
  };
  if (err) return <div className="od-empty"><b>Couldn't load the Review list</b>{err}</div>;
  if (!data) return <div className="od-skel" aria-busy="true" />;
  const h = data.health || {};
  const kinds = [...new Set(data.items.map((i) => i.kind))];
  const items = data.items.filter((i) => kind === 'all' || i.kind === kind);
  const tasks = h.zohoTasks || {};
  return (
    <div className="od-det od-reports">
      <header className="od-bh">
        <div className="od-bh-top"><h2>Review list</h2><span className="od-small">what the order flow could not finish on its own</span></div>
        <div className="od-bh-stats">
          <div className={h.review?.open ? 'pend' : ''}><b>{h.review?.open ?? 0}</b><small>Open</small></div>
          <div><b>{h.today?.newOrders ?? 0}</b><small>New orders today</small></div>
          <div><b>{h.today?.medianArrivalSec != null ? `${h.today.medianArrivalSec} s` : '—'}</b><small>Shopify → desk</small></div>
          <div className={tasks.failed || tasks.review ? 'pend' : ''}><b>{(tasks.waiting || 0) + (tasks.decided || 0) + (tasks.sending || 0)}</b><small>Zoho to-dos waiting</small></div>
        </div>
        {h.lastCheck && <p className="od-bh-meta">Last nightly check {when(h.lastCheck.atMs)}: {h.lastCheck.shopifyOrders} Shopify orders and {h.lastCheck.deskOrders} desk orders in the last {h.lastCheck.days} days, {h.lastCheck.found} problems{h.lastCheck.autoClosed ? `, ${h.lastCheck.autoClosed} fixed since` : ''}.{h.today?.errors ? ` · ${h.today.errors} Shopify imports failed today` : ''}</p>}
      </header>
      <div className="od-stage-tabs" role="tablist" aria-label="Review state">
        {STATE_TABS.map(([k, l]) => <button key={k} role="tab" aria-selected={state === k} onClick={() => { setState(k); setKind('all'); }}>{l}</button>)}
      </div>
      {kinds.length > 1 && (
        <div className="od-stage-tabs" role="tablist" aria-label="Problem type">
          <button role="tab" aria-selected={kind === 'all'} onClick={() => setKind('all')}>All <span>{data.items.length}</span></button>
          {kinds.map((k) => <button key={k} role="tab" aria-selected={kind === k} onClick={() => setKind(k)}>{KIND_LABEL[k] || k} <span>{data.items.filter((i) => i.kind === k).length}</span></button>)}
        </div>
      )}
      {items.length === 0 && <div className="od-empty"><b>Nothing here</b>{state === 'open' ? 'The order flow has nothing waiting for a person.' : ''}</div>}
      <ul className="od-rep-list">
        {items.map((i) => {
          const closed = i.state !== 'open';
          return (
            <li key={i.key} className={`od-rep ${i.state === 'open' ? (i.severity === 'high' ? 'open' : 'fixing') : 'fixed'}`}>
              <div className="od-rep-top">
                {i.orderId ? <button className="od-link od-mono" onClick={() => onOpenOrder?.(i.orderId)}>#{i.orderNumber || i.orderId}</button> : <b className="od-mono">{i.orderNumber ? `#${i.orderNumber}` : '—'}</b>}
                <span className="od-rep-type">{i.title}</span>
                <span className={`od-rep-st ${i.severity === 'high' ? 'open' : i.severity === 'medium' ? 'fixing' : ''}`}>{i.severity}</span>
                <small>{when(i.updatedAtMs)}{i.reopenedAtMs ? ' · reopened' : ''}</small>
              </div>
              <p className="od-rep-truth">{i.reason}</p>
              {i.action && <p className="od-rep-snap"><span>What to do:</span> {i.action}</p>}
              {closed ? (
                <p className="od-small">{i.state === 'resolved' ? 'Resolved' : 'Dismissed'} by {i.resolvedBy} · {when(i.resolvedAtMs)}{i.note ? `: ${i.note}` : ''} {canAct && <button className="od-link" disabled={busy === i.key} onClick={() => move(i.key, 'open')}>Reopen</button>}</p>
              ) : canAct && (
                <div className="od-rep-act">
                  <input className="od-input" aria-label={`What was done for ${i.orderNumber || i.key}`} value={notes[i.key] || ''} onChange={(e) => setNotes((n) => ({ ...n, [i.key]: e.target.value }))} placeholder="One line: what was done (needed to close)" />
                  <button className="od-btn od-pri" disabled={busy === i.key || (notes[i.key] || '').trim().length < 3} onClick={() => move(i.key, 'resolved')}>Resolved</button>
                  <button className="od-btn" disabled={busy === i.key || (notes[i.key] || '').trim().length < 3} onClick={() => move(i.key, 'dismissed')}>Not a problem</button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
